import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface DocumentEmailWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  unaId: string;
  preSelectedDocumentId: string;
  onSuccess?: () => void;
  prePopulatedRecipient?: {
    name: string;
    email: string;
  };
}

const steps = [
  { id: 1, name: "Select Recipient" },
  { id: 2, name: "Review & Send" },
];

export default function DocumentEmailWizard({
  open,
  onOpenChange,
  taskId,
  unaId,
  preSelectedDocumentId,
  onSuccess,
  prePopulatedRecipient,
}: DocumentEmailWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documentName, setDocumentName] = useState("");
  
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (open) {
      // Initialize from pre-populated recipient if provided
      if (prePopulatedRecipient) {
        setRecipientName(prePopulatedRecipient.name);
        setRecipientEmail(prePopulatedRecipient.email);
      } else {
        // Reset if no pre-populated data
        setRecipientName("");
        setRecipientEmail("");
      }
      setMessage("");
      loadDocumentName();
    }
  }, [open, prePopulatedRecipient, preSelectedDocumentId]);

  const loadDocumentName = async () => {
    try {
      const { data, error } = await supabase
        .from("generated_documents")
        .select("document_name")
        .eq("id", preSelectedDocumentId)
        .single();

      if (error) throw error;
      setDocumentName(data.document_name);
    } catch (error) {
      console.error("Error loading document:", error);
    }
  };

  const canProceed = () => {
    if (currentStep === 1) {
      return recipientEmail && recipientName;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("send-document-email", {
        body: {
          documentId: preSelectedDocumentId,
          recipientEmail,
          recipientName,
          message: message || undefined,
          taskId,
          unaId,
        },
      });

      if (error) throw error;

      toast.success(`Document sent to ${recipientEmail}`);
      onSuccess?.();
      onOpenChange(false);
      
      // Reset wizard
      setCurrentStep(1);
      setRecipientEmail("");
      setRecipientName("");
      setMessage("");
    } catch (error: any) {
      console.error("Error sending document email:", error);
      toast.error(error.message || "Failed to send document");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Email Document</DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {steps.map((step, idx) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                currentStep >= step.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                {step.id}
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-12 h-0.5 mx-2 ${
                  currentStep > step.id ? 'bg-primary' : 'bg-muted'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[400px]">
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Mail className="h-4 w-4" />
                  Document to send:
                </div>
                <div className="font-medium">{documentName}</div>
              </div>

              {prePopulatedRecipient ? (
                <div className="bg-muted/50 p-4 rounded-lg border">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Send className="h-4 w-4" />
                    Sending to:
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="text-xs text-muted-foreground">Name</div>
                      <div className="font-medium">{recipientName}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Email</div>
                      <div className="font-medium">{recipientEmail}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="recipientName">Recipient Name *</Label>
                    <Input
                      id="recipientName"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Enter recipient's name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="recipientEmail">Recipient Email *</Label>
                    <Input
                      id="recipientEmail"
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="Enter recipient's email"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="message">Message (Optional)</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a personal message to include with the document..."
                  rows={4}
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-6 rounded-lg border space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Document</div>
                  <div className="font-medium">{documentName}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">Recipient</div>
                  <div className="font-medium">{recipientName}</div>
                  <div className="text-sm text-muted-foreground">{recipientEmail}</div>
                </div>

                {message && (
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Message</div>
                    <div className="text-sm whitespace-pre-wrap bg-background p-3 rounded border">
                      {message}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex gap-2">
                  <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900 dark:text-blue-100">
                    <div className="font-medium mb-1">Ready to send</div>
                    <p>The document will be sent as a PDF attachment to {recipientEmail}. They will receive it immediately.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || isSubmitting}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep < steps.length ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed() || isSubmitting}
            >
              Next
              <Mail className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              <Send className="w-4 h-4 mr-2" />
              {isSubmitting ? "Sending..." : "Send Document"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
